import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../../app.settings';

import { notifyAndTrackError } from '../../../../common/error';
import { isEmpty } from '../../../../common/functions';
import { successMsg } from '../../../../common/messages';

import { AuditInfoComponent, CanComponentDeactivate } from '../../../../components';

import { AppInsightsService } from '../../../../services/app-insights.service';
import { DeliveryDayAdminService, IDeliveryUnavailableDate } from '../../../../services/insecta';

@Component({
  selector: 'tab-undeliverable-dates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DatePickerModule,
    DialogModule,
    FloatLabelModule,
    FieldsetModule,
    FormsModule,
    InputTextModule,
    RouterModule,
    SelectModule,
    TableModule,
    TabsModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    AuditInfoComponent
  ],
  templateUrl: 'tab-undeliverable-dates.component.html'
})
export class TabUndeliverableDatesComponent implements OnDestroy, OnInit, CanComponentDeactivate {
  dialogVars: any = {
    maxDate: undefined,
    minDate: undefined,
    visible: false
  };
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;
  pageVars: any = {
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Site Catalog & Settings' }],
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false
  };

  @Input() currentUser: string = ``;
  @Input() locationId: string = this.emptyGuid;
  @Output('isReady') isReady = new EventEmitter<boolean>();

  undeliverableDate: IDeliveryUnavailableDate = <IDeliveryUnavailableDate>{};
  undeliverableDates: IDeliveryUnavailableDate[] = [];
  undeliverableYear: number = new Date().getFullYear();
  years: number[] = [];

  constructor(
    private deliveryDayService: DeliveryDayAdminService,
    private appInsights: AppInsightsService,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:UndeliverableDatesTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#tabundeliverabledates`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.locationId = this.emptyGuid;
    this.pageVars = {
      isReady: false
    };
    this.undeliverableDate = <IDeliveryUnavailableDate>{};
    this.undeliverableDates = [];
  }

  ngOnInit() {
    this.isReady.emit(false);
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);
    this.pageVars.isReady = false;

    this.undeliverableYear = new Date().getFullYear();
    this.years = [this.undeliverableYear, this.undeliverableYear + 1];

    const lowerBound = `${this.years[0]}-01-01`;
    const upperBound = `${this.years[0]}-12-31`;

    const request = {
      unavailableDates: this.deliveryDayService.getUnavailableDatesByRangeForAll(this.locationId, lowerBound, upperBound, true)
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.undeliverableDates = response.unavailableDates;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabUndeliverableDatesComponent', action: 'Init:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
        this.isReady.emit(true);
      });
  }

  changeYear() {
    if (this.pageVars.isReady) {
      this.getUndeliverableDates();
    }
  }

  getUndeliverableDates() {
    this.isReady.emit(false);
    this.pageVars.isReady = false;

    const lowerBound = `${this.undeliverableYear}-01-01`;
    const upperBound = `${this.undeliverableYear}-12-31`;

    firstValueFrom(this.deliveryDayService.getUnavailableDatesByRangeForAll(this.locationId, lowerBound, upperBound, true))
      .then((response) => (this.undeliverableDates = <IDeliveryUnavailableDate[]>response))
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabUndeliverableDatesComponent', action: 'GetDates:Error' });
      })
      .finally(() => {
        this.pageVars.isEditing = false;
        this.pageVars.isReady = true;
        this.isReady.emit(true);
      });
  }

  newUndeliverableDate() {
    this.dialogVars.maxDate = new Date(this.undeliverableYear, 11, 31);
    this.dialogVars.minDate = new Date(this.undeliverableYear, 0, 1);
    if (new Date() >= this.dialogVars.minDate) {
      const d = new Date();
      this.dialogVars.minDate = new Date(d.setDate(d.getDate() + 1));
    }

    this.undeliverableDate = <IDeliveryUnavailableDate>{
      date: null,
      isActive: true,
      action: 'INS',
      actionBy: this.currentUser,
      actionOn: new Date(Date.now())
    };
    this.dialogVars.visible = true;
  }

  tglUndeliverableDates(d: IDeliveryUnavailableDate) {
    firstValueFrom(this.deliveryDayService.updateUnavailableDate(this.locationId, d.date, d.isActive))
      .then(() => {
        const idx = this.undeliverableDates.findIndex((x) => x.date === d.date);
        this.undeliverableDates[idx].action = 'UPD';
        this.undeliverableDates[idx].actionBy = this.currentUser;
        this.undeliverableDates[idx].actionOn = new Date();

        successMsg(this.msgService, 'Undeliverable Date has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: 'TabUndeliverableDatesComponent',
          action: 'ToggleUnavailableDate:Error'
        });
      });
  }

  exitNewDateDlg() {
    this.dialogVars.visible = false;
  }

  isValidNewDataDlg(): boolean {
    const dateValue = this.undeliverableDate?.date;
    if (isEmpty(dateValue)) return false;
    if (dateValue === '') return false;

    const parsedDate = new Date(dateValue);
    if (isNaN(parsedDate.getTime())) return false;
    if (parsedDate.getFullYear() != this.undeliverableYear) return false;
    return true;
  }

  saveNewDateDlg() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Insert`, { undeliverableDate: this.undeliverableDate });
    this.undeliverableDate.date = this.toDateOnly(this.undeliverableDate.date);

    firstValueFrom(this.deliveryDayService.addUnavailableDate(this.locationId, this.undeliverableDate.date))
      .then(() => {
        this.undeliverableDates = [...this.undeliverableDates, <IDeliveryUnavailableDate>this.undeliverableDate].sort((a, b) =>
          a.date > b.date ? 1 : -1
        );
        successMsg(this.msgService, 'Undeliverable Date has been created');
        this.undeliverableDate = <IDeliveryUnavailableDate>{};
        this.dialogVars.visible = false;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabUndeliverableDatesComponent', action: 'Dialog:Insert:Error' });
      });
  }

  toDateOnly(x: any): string {
    const y = x.getFullYear();
    const m = x.getMonth() + 1;
    const d = x.getDate();

    const s = `${y}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
    return s;
  }

  isDisabled(date: any) {
    if (isEmpty(date)) return false;

    const inputDate = new Date(date + 'T00:00:00Z');
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    return inputDate < today;
  }
}
